import API from '../config/api';
import React, { useEffect, useState } from 'react';

import axios from 'axios';

import { Container, Row, Col, Card, Form } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';

import { confirmToast } from '../utils/toast';

/* The happy path an order walks, in order — drives both the stepper on each
   card and which options stay selectable in the status dropdown. */
const STATUS_FLOW = [
  'Confirmed',
  'Processing',
  'Shipped',
  'Out For Delivery',
  'Delivered'
];

/* Stages before the goods leave the warehouse — no partner to assign yet */
const PRE_SHIPMENT = ['Confirmed', 'Processing'];

/* One place where a status becomes a colour and an icon. Everything tinted per
   order — strip, pill, stepper, hover wash — reads its accent from here. */
const STATUS_META = {
  Processing: { color: '#f59e0b', icon: '⏳' },
  Shipped: { color: '#3b82f6', icon: '🚚' },
  'Out For Delivery': { color: '#06b6d4', icon: '🛵' },
  Delivered: { color: '#22c55e', icon: '✅' },
  Cancelled: { color: '#ef4444', icon: '✖' }
};

const metaFor = (status) => STATUS_META[status] || STATUS_META.Processing;

function AdminOrdersPage() {
  const [orders, setOrders] = useState([]);

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [partners, setPartners] = useState([]);

  useEffect(() => {
    fetchOrders();
    fetchPartners();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get(`${API}/orders`);

      setOrders(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPartners = async () => {
    try {
      const { data } = await axios.get(
        `${API}/delivery/partners/approved`
      );

      setPartners(data);
    } catch (error) {
      console.log(error);
    }
  };

  const assignPartner = async (id, deliveryPartner) => {
    try {
      await axios.put(`${API}/orders/${id}/assign`, {
        deliveryPartner: deliveryPartner || null
      });

      fetchOrders();
    } catch (error) {
      console.log(error);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${API}/orders/${id}`,

        {
          status,

          /* Admin can set any status directly, bypassing the delivery OTP. */
          override: true
        }
      );

      fetchOrders();
    } catch (error) {
      console.log(error);
    }
  };

  const getStatusColor = (status) => metaFor(status).color;

  /*
    Whether the payment for an order has actually been received.
    - paymentId present  → prepaid online (Razorpay) → money received
    - codCollected true  → cash collected on delivery → money received
    - otherwise          → cash-on-delivery still to be collected
  */
  const getPayment = (order) => {
    if (order.paymentId) {
      return {
        label: 'Paid Online',
        icon: '💳',
        color: 'success',
        received: true,
        note: order.paymentId
      };
    }
    if (order.codCollected) {
      return {
        label: 'COD Collected',
        icon: '✅',
        color: 'success',
        received: true,
        note: 'Cash received on delivery'
      };
    }
    return {
      label: 'Payment Pending',
      icon: '💵',
      color: 'warning',
      received: false,
      note: 'Cash to be collected on delivery'
    };
  };

  const isReceived = (o) => Boolean(o.paymentId || o.codCollected);
  const receivedTotal = orders
    .filter(isReceived)
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const pendingTotal = orders
    .filter((o) => !isReceived(o))
    .reduce((sum, o) => sum + (o.total || 0), 0);
  const receivedCount = orders.filter(isReceived).length;
  const pendingCount = orders.length - receivedCount;
  const deleteOrder = async (id) => {
    const confirmDelete = await confirmToast('Delete this order?');

    if (!confirmDelete) return;

    try {
      await axios.delete(`${API}/orders/${id}`);

      fetchOrders();
    } catch (error) {
      console.log(error);
    }
  };

  const filteredOrders = orders.filter((order) => {
    const keyword = search.toLowerCase();
    const matchesSearch =
      order.customerName?.toLowerCase().includes(keyword) ||
      order.phone?.toLowerCase().includes(keyword) ||
      order._id?.toLowerCase().includes(keyword);
    const matchesStatus = !statusFilter || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <>
      <CustomNavbar />

      <div className="admin-orders-wrap">
        <Container>
          <Card className="shadow border-0 mb-4 ao-hero">
            <span className="ao-hero-aurora" aria-hidden="true" />

            {/* Motes drifting up through the banner */}
            <span className="ao-motes" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <span className="ao-mote" key={i} />
              ))}
            </span>

            <Card.Body className="position-relative p-4">
              <h1 className="fw-bold mb-1 ao-hero-title">
                <span className="ao-hero-icon">📦</span>
                <span className="ao-hero-title-text">Order Management</span>
              </h1>

              <p className="mb-0 ao-hero-text">
                Manage all customer orders — {orders.length} total
              </p>
            </Card.Body>
          </Card>

          {/* Payment summary — at-a-glance "is the money in?" */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm h-100 ao-pay-summary received">
                <Card.Body className="d-flex align-items-center gap-3">
                  <div className="ao-pay-emoji">💰</div>
                  <div>
                    <div className="ao-pay-amount text-success">
                      ₹{receivedTotal.toLocaleString()}
                    </div>
                    <div className="ao-pay-caption">
                      Amount Received
                      <span className="ao-pay-count">{receivedCount} orders</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm h-100 ao-pay-summary pending">
                <Card.Body className="d-flex align-items-center gap-3">
                  <div className="ao-pay-emoji">⏳</div>
                  <div>
                    <div className="ao-pay-amount text-warning">
                      ₹{pendingTotal.toLocaleString()}
                    </div>
                    <div className="ao-pay-caption">
                      Payment Pending
                      <span className="ao-pay-count">{pendingCount} orders</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} md={4}>
              <Card className="border-0 shadow-sm h-100 ao-pay-summary total">
                <Card.Body className="d-flex align-items-center gap-3">
                  <div className="ao-pay-emoji">🧾</div>
                  <div>
                    <div className="ao-pay-amount text-dark">
                      ₹{(receivedTotal + pendingTotal).toLocaleString()}
                    </div>
                    <div className="ao-pay-caption">
                      Total Order Value
                      <span className="ao-pay-count">{orders.length} orders</span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filters */}
          <div className="ao-search-row">
            <div className="ao-search">
              <span className="ao-search-icon" aria-hidden="true">
                🔍
              </span>

              <Form.Control
                className="ao-search-input"
                placeholder="Search by customer, phone or order ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              {search && (
                <button
                  type="button"
                  className="ao-search-clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  ×
                </button>
              )}
            </div>

            <span className="ao-result-count">
              <span className="ao-result-num">{filteredOrders.length}</span>
              shown
            </span>
          </div>

          {/* Status chips replace the dropdown — each carries its own colour
              and a live count, so the mix is readable at a glance. */}
          <div className="ao-chips">
            <button
              type="button"
              className={`ao-chip is-all${!statusFilter ? ' is-active' : ''}`}
              onClick={() => setStatusFilter('')}
            >
              ✨ All
              <span className="ao-chip-count">{orders.length}</span>
            </button>

            {Object.keys(STATUS_META).map((status) => {
              const count = orders.filter((o) => o.status === status).length;

              return (
                <button
                  key={status}
                  type="button"
                  className={`ao-chip${
                    statusFilter === status ? ' is-active' : ''
                  }`}
                  style={{ '--chip': STATUS_META[status].color }}
                  onClick={() =>
                    setStatusFilter(statusFilter === status ? '' : status)
                  }
                >
                  {STATUS_META[status].icon} {status}
                  <span className="ao-chip-count">{count}</span>
                </button>
              );
            })}
          </div>

          {filteredOrders.length === 0 ? (
            <Card
              className="border-0 shadow-sm text-center py-5"
              style={{ borderRadius: '18px' }}
            >
              <Card.Body>
                <div style={{ fontSize: '3rem' }}>🔎</div>
                <h5 className="fw-bold mt-2">No Orders Found</h5>
                <p className="text-muted mb-0">
                  Try adjusting your search or status filter.
                </p>
              </Card.Body>
            </Card>
          ) : (
            filteredOrders.map((order, index) => {
              const pay = getPayment(order);
              const stepIndex = STATUS_FLOW.indexOf(order.status);

              return (
              <Card
                key={order._id}
                className="border-0 shadow-sm mb-3 ao-order-card"
                style={{
                  '--o-accent': getStatusColor(order.status),
                  /* capped so the 40th card doesn't wait two seconds */
                  '--i': Math.min(index, 12)
                }}
              >
                <span className="ao-card-strip" aria-hidden="true" />

                <Card.Body className="p-3 p-md-4">
                  {/* Header */}
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="ao-order-id">
                        #{order._id.slice(-6)}
                      </span>

                      <span className="ao-status-pill">
                        <span className="ao-status-dot" aria-hidden="true" />
                        {metaFor(order.status).icon} {order.status}
                      </span>

                      <span
                        className={`ao-pay-pill ${
                          pay.received ? 'is-received' : 'is-pending'
                        }`}
                      >
                        {pay.icon} {pay.label}
                      </span>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                      {/* Where the order is along the delivery flow */}
                      {order.status !== 'Cancelled' && (
                        <span
                          className="ao-steps"
                          aria-label={`Step ${stepIndex + 1} of ${STATUS_FLOW.length}`}
                        >
                          {STATUS_FLOW.map((s, i) => (
                            <span
                              key={s}
                              className={`ao-step${i <= stepIndex ? ' is-done' : ''}${
                                i === stepIndex ? ' is-current' : ''
                              }`}
                              title={s}
                            />
                          ))}
                        </span>
                      )}

                      <small className="text-muted">
                        📅 {new Date(order.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>

                  {/* Details grid */}
                  <Row className="g-3">
                    <Col md={6} lg={3}>
                      <div className="ao-label">Customer</div>
                      <strong className="d-block">{order.customerName}</strong>
                      <small className="text-muted">{order.phone}</small>
                    </Col>

                    <Col md={6} lg={3}>
                      <div className="ao-label">Products</div>
                      {order.orderItems?.map((item, index) => (
                        <div key={index} className="small">
                          {item.name}{' '}
                          <span className="text-muted">× {item.quantity}</span>
                        </div>
                      ))}
                    </Col>

                    <Col md={6} lg={3}>
                      <div className="ao-label">Delivery Address</div>
                      <div className="small">{order.address}</div>
                      <small className="text-muted">
                        {order.city} - {order.pincode}
                      </small>
                    </Col>

                    <Col md={6} lg={3}>
                      <div className="ao-label">Total</div>
                      <strong className="text-success fs-5">
                        ₹{order.total}
                      </strong>

                      {order.couponCode && (
                        <div className="mt-2 p-2 rounded ao-coupon">
                          <small className="d-block">
                            🎟 <strong>{order.couponCode}</strong> (
                            {order.discount}% off)
                          </small>
                          <small className="text-danger">
                            Saved ₹{order.discountAmount}
                          </small>
                        </div>
                      )}

                      <div className="ao-label mt-2">Payment</div>
                      {(() => {
                        const pay = getPayment(order);
                        return (
                          <div
                            className={
                              'ao-pay-chip ' +
                              (pay.received ? 'is-received' : 'is-pending')
                            }
                          >
                            <span className="fw-bold">
                              {pay.icon}{' '}
                              {pay.received ? 'Received' : 'Not Received'}
                            </span>
                            <small
                              className="d-block"
                              style={{ wordBreak: 'break-word' }}
                            >
                              {order.paymentId
                                ? 'Txn: ' + order.paymentId
                                : pay.note}
                            </small>
                          </div>
                        );
                      })()}
                    </Col>
                  </Row>

                  <hr className="my-3" />

                  {/* Actions */}
                  <Row className="g-2 align-items-end">
                    <Col md={4}>
                      <div className="ao-label">Update Status</div>
                      <Form.Select
                        value={order.status}
                        /* Cancelled is terminal — nothing left to update */
                        disabled={order.status === 'Cancelled'}
                        onChange={(e) =>
                          updateStatus(order._id, e.target.value)
                        }
                      >
                        {order.status === 'Cancelled' && (
                          <option value="Cancelled">Cancelled</option>
                        )}

                        {/* Uses the shared flow, so adding a stage in one
                            place cannot leave this list behind */}
                        {STATUS_FLOW.map((s, i, flow) => (
                          <option
                            key={s}
                            value={s}
                            disabled={i < flow.indexOf(order.status)}
                          >
                            {s}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>

                    <Col md={4}>
                      <div className="ao-label">Delivery Partner</div>
                      <Form.Select
                        value={order.deliveryPartner || ''}
                        disabled={PRE_SHIPMENT.includes(order.status)}
                        onChange={(e) =>
                          assignPartner(order._id, e.target.value)
                        }
                      >
                        <option value="">Unassigned</option>
                        {partners.map((p) => (
                          <option key={p._id} value={p._id}>
                            {p.name} ({p.vehicleType || 'Partner'})
                          </option>
                        ))}
                      </Form.Select>
                      {PRE_SHIPMENT.includes(order.status) && (
                        <small className="text-muted">
                          Mark as Shipped to assign a partner
                        </small>
                      )}
                    </Col>

                    <Col md={4} className="text-md-end">
                      <button
                        type="button"
                        className="ao-del-btn mt-2 mt-md-0"
                        disabled={order.status === 'Delivered'}
                        onClick={() => deleteOrder(order._id)}
                      >
                        <span className="ao-del-ico" aria-hidden="true">
                          🗑
                        </span>
                        Delete Order
                      </button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              );
            })
          )}
        </Container>
      </div>

      <style>{adminOrdersStyles}</style>
    </>
  );
}

const adminOrdersStyles = `
  .admin-orders-wrap {
    background: radial-gradient(720px 320px at 8% -5%, rgba(124,58,237,0.12), transparent 70%), radial-gradient(720px 320px at 92% 105%, rgba(234,88,12,0.14), transparent 70%), linear-gradient(180deg, #faf5ff 0%, #fff7ed 55%, #fef3c7 100%);
    min-height: 100vh;
    padding: clamp(24px, 4vw, 40px) 0;
  }

  /* ===== Sanctum banner ===== */
  .ao-hero {
    position: relative;
    overflow: hidden;
    isolation: isolate;
    border-radius: 22px;
    color: #fff;
    background: linear-gradient(120deg, #0f172a, #3b0764 28%, #7c2d12 62%, #ea580c);
    background-size: 220% 220%;
    border: 1px solid rgba(253, 230, 138, 0.25) !important;
    animation: aoHeroDrift 18s ease-in-out infinite alternate;
  }
  @keyframes aoHeroDrift {
    from { background-position: 0% 50%; }
    to   { background-position: 100% 50%; }
  }
  /* Gold threads along both edges, running opposite ways */
  .ao-hero::before,
  .ao-hero::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 2px;
    z-index: 3;
    background: linear-gradient(90deg, transparent, #fde68a 20%, #f59e0b 50%, #fde68a 80%, transparent);
    background-size: 220% 100%;
    animation: aoThread 3.6s linear infinite;
  }
  .ao-hero::before { top: 0; }
  .ao-hero::after { bottom: 0; animation-direction: reverse; }
  @keyframes aoThread {
    to { background-position: 220% 0; }
  }

  .ao-hero-aurora {
    position: absolute;
    inset: -40%;
    z-index: 0;
    pointer-events: none;
    background:
      radial-gradient(36% 44% at 24% 30%, rgba(251,191,36,0.40), transparent 62%),
      radial-gradient(32% 40% at 78% 26%, rgba(168,85,247,0.34), transparent 62%),
      radial-gradient(40% 46% at 58% 84%, rgba(234,88,12,0.38), transparent 62%);
    filter: blur(30px);
    animation: aoAurora 22s ease-in-out infinite alternate;
    will-change: transform;
  }
  @keyframes aoAurora {
    0%   { transform: translate3d(-4%, -3%, 0) rotate(0deg)  scale(1.05); }
    50%  { transform: translate3d(5%, 4%, 0)   rotate(8deg)  scale(1.18); }
    100% { transform: translate3d(-3%, 5%, 0)  rotate(-6deg) scale(1.1); }
  }

  .ao-motes { position: absolute; inset: 0; z-index: 1; pointer-events: none; overflow: hidden; }
  .ao-mote {
    position: absolute;
    bottom: -12px;
    width: 6px; height: 6px;
    border-radius: 50%;
    background: rgba(253, 230, 138, 0.9);
    box-shadow: 0 0 12px rgba(251, 191, 36, 0.9);
    opacity: 0;
    animation: aoMote 12s linear infinite;
  }
  .ao-mote:nth-child(1) { left: 8%;  animation-delay: 0s; }
  .ao-mote:nth-child(2) { left: 21%; animation-delay: 4.4s; width: 4px; height: 4px; }
  .ao-mote:nth-child(3) { left: 34%; animation-delay: 1.6s; }
  .ao-mote:nth-child(4) { left: 47%; animation-delay: 7.3s; width: 5px; height: 5px; }
  .ao-mote:nth-child(5) { left: 60%; animation-delay: 2.9s; }
  .ao-mote:nth-child(6) { left: 73%; animation-delay: 9.1s; width: 4px; height: 4px; }
  .ao-mote:nth-child(7) { left: 86%; animation-delay: 5.7s; }
  .ao-mote:nth-child(8) { left: 95%; animation-delay: 3.2s; width: 5px; height: 5px; }
  @keyframes aoMote {
    0%   { opacity: 0; transform: translateY(0) translateX(0) scale(0.6); }
    12%  { opacity: 1; }
    80%  { opacity: 0.6; }
    100% { opacity: 0; transform: translateY(-190px) translateX(22px) scale(1.1); }
  }

  .ao-hero .card-body { z-index: 2; }
  .ao-hero-title { display: flex; align-items: center; gap: 10px; }
  .ao-hero-icon {
    display: inline-block;
    animation: aoIconBob 3.2s ease-in-out infinite;
  }
  @keyframes aoIconBob {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-6px) rotate(-7deg); }
  }
  .ao-hero-title-text {
    background: linear-gradient(100deg,
      #ffffff 0%, #ffffff 32%, #fde68a 46%, #fbbf24 52%, #fde68a 58%, #ffffff 74%, #ffffff 100%);
    background-size: 250% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    animation: aoTitleShine 5.5s ease-in-out infinite;
  }
  @keyframes aoTitleShine {
    0%, 100% { background-position: 130% 0; }
    50%      { background-position: -30% 0; }
  }
  .ao-hero-text { opacity: 0.85; }

  /* ===== Search ===== */
  .ao-search-row {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }
  .ao-search {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1 1 340px;
    max-width: 560px;
    padding: 4px 14px;
    border-radius: 50px;
    background: #fff;
    border: 2px solid rgba(245, 158, 11, 0.26);
    box-shadow: 0 10px 28px rgba(194, 65, 12, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  .ao-search:focus-within {
    border-color: #f59e0b;
    box-shadow: 0 14px 36px rgba(245, 158, 11, 0.28);
  }
  .ao-search-icon { flex: none; font-size: 1rem; }
  .ao-search-input {
    border: none !important;
    padding: 9px 0;
    box-shadow: none !important;
    background: transparent;
  }
  .ao-search-clear {
    flex: none;
    border: none;
    width: 22px; height: 22px;
    border-radius: 50%;
    line-height: 1;
    font-size: 1.05rem;
    color: #b45309;
    background: #fff7ed;
    cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease;
  }
  .ao-search-clear:hover { background: #fde68a; transform: rotate(90deg); }
  .ao-result-count {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #9a3412;
  }
  .ao-result-num {
    font-weight: 800;
    color: #fff;
    background: linear-gradient(135deg, #ea580c, #f59e0b);
    border-radius: 30px;
    padding: 2px 11px;
    box-shadow: 0 4px 12px rgba(234, 88, 12, 0.3);
  }

  /* ===== Status chips ===== */
  .ao-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-bottom: 22px;
  }
  .ao-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 9px 7px 15px;
    border-radius: 50px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid var(--chip, #f59e0b);
    color: var(--chip, #9a3412);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
  }
  .ao-chip.is-all { --chip: #9a3412; }
  .ao-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 18px color-mix(in srgb, var(--chip, #ea580c) 30%, transparent);
  }
  .ao-chip:active { transform: translateY(0) scale(0.96); }
  .ao-chip.is-active {
    color: #fff;
    background: var(--chip, #ea580c);
    box-shadow: 0 8px 20px color-mix(in srgb, var(--chip, #ea580c) 45%, transparent);
    animation: aoChipPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes aoChipPop {
    0%   { transform: scale(0.88); }
    55%  { transform: scale(1.08); }
    100% { transform: scale(1); }
  }
  .ao-chip-count {
    min-width: 22px;
    padding: 1px 8px;
    border-radius: 30px;
    font-size: 0.72rem;
    font-weight: 800;
    background: color-mix(in srgb, var(--chip, #ea580c) 14%, #ffffff);
    color: var(--chip, #9a3412);
  }
  .ao-chip.is-active .ao-chip-count {
    background: rgba(255, 255, 255, 0.9);
  }
  /* Fallback where color-mix is unsupported */
  @supports not (background: color-mix(in srgb, red 10%, blue)) {
    .ao-chip-count { background: #fff7ed; color: #9a3412; }
    .ao-chip:hover { box-shadow: 0 8px 18px rgba(234, 88, 12, 0.25); }
    .ao-chip.is-active { box-shadow: 0 8px 20px rgba(234, 88, 12, 0.4); }
  }

  /* ===== Order cards ===== */
  .ao-order-card {
    position: relative;
    overflow: hidden;
    border-radius: 18px;
    border: 1px solid rgba(245, 158, 11, 0.14) !important;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: aoCardIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) backwards;
    animation-delay: calc(var(--i, 0) * 55ms);
  }
  @keyframes aoCardIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  /* The old inline 6px border, but alive — a gradient of the status colour
     with light travelling down it */
  .ao-card-strip {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 6px;
    background: linear-gradient(180deg,
      var(--o-accent, #f59e0b),
      color-mix(in srgb, var(--o-accent, #f59e0b) 45%, #ffffff),
      var(--o-accent, #f59e0b));
    background-size: 100% 220%;
    animation: aoStrip 3.2s linear infinite;
  }
  @keyframes aoStrip {
    to { background-position: 0 220%; }
  }
  @supports not (background: color-mix(in srgb, red 10%, blue)) {
    .ao-card-strip { background: var(--o-accent, #f59e0b); animation: none; }
  }
  .ao-order-card .card-body { padding-left: 22px; }
  .ao-order-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px color-mix(in srgb, var(--o-accent, #ea580c) 26%, transparent) !important;
  }
  @supports not (box-shadow: 0 0 0 color-mix(in srgb, red 10%, blue)) {
    .ao-order-card:hover { box-shadow: 0 16px 32px rgba(194, 65, 12, 0.16) !important; }
  }

  .ao-order-id {
    font-weight: 800;
    font-size: 1.05rem;
    color: #1e293b;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  /* Status pill — tinted from the order's own accent */
  .ao-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 4px 13px;
    border-radius: 30px;
    font-size: 0.76rem;
    font-weight: 700;
    color: #fff;
    background: var(--o-accent, #f59e0b);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--o-accent, #ea580c) 40%, transparent);
  }
  .ao-status-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: aoDot 1.7s ease-in-out infinite;
  }
  @keyframes aoDot {
    0%, 100% { opacity: 0.45; transform: scale(0.8); }
    50%      { opacity: 1;    transform: scale(1.3); }
  }

  .ao-pay-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 13px;
    border-radius: 30px;
    font-size: 0.76rem;
    font-weight: 700;
    border: 1px solid transparent;
  }
  .ao-pay-pill.is-received {
    color: #15803d;
    background: #f0fdf4;
    border-color: #bbf7d0;
  }
  .ao-pay-pill.is-pending {
    color: #b45309;
    background: #fffbeb;
    border-color: #fde68a;
    animation: aoAlert 2.4s ease-in-out infinite;
  }
  @keyframes aoAlert {
    0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.45); }
    50%      { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
  }

  /* ===== Delivery stepper ===== */
  .ao-steps {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .ao-step {
    width: 22px;
    height: 5px;
    border-radius: 4px;
    background: #e7e2da;
    transition: background 0.35s ease, width 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .ao-step.is-done { background: var(--o-accent, #f59e0b); }
  .ao-step.is-current {
    width: 32px;
    animation: aoStepGlow 1.9s ease-in-out infinite;
  }
  @keyframes aoStepGlow {
    0%, 100% { opacity: 0.6; }
    50%      { opacity: 1; }
  }

  /* ===== Delete ===== */
  .ao-del-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 1px solid #fecaca;
    border-radius: 12px;
    padding: 7px 16px;
    font-size: 0.82rem;
    font-weight: 700;
    color: #b91c1c;
    background: #fff;
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease,
      border-color 0.25s ease;
  }
  .ao-del-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #991b1b, #dc2626 55%, #ef4444);
    box-shadow: 0 10px 22px rgba(220, 38, 38, 0.42);
  }
  .ao-del-btn:active:not(:disabled) { transform: translateY(0) scale(0.96); }
  .ao-del-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: #a8a29e;
    border-color: #e7e5e4;
  }
  .ao-del-ico { display: inline-block; }
  .ao-del-btn:hover:not(:disabled) .ao-del-ico {
    animation: aoShake 0.55s ease-in-out infinite;
  }
  @keyframes aoShake {
    0%, 100% { transform: translateX(0) rotate(0deg); }
    30%      { transform: translateX(-2px) rotate(-14deg); }
    65%      { transform: translateX(2px) rotate(11deg); }
  }

  /* Selects inside the cards */
  .ao-order-card .form-select {
    border-radius: 12px;
    padding: 9px 14px;
    border: 1px solid #eadfce;
    background-color: #fffdfa;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .ao-order-card .form-select:focus {
    border-color: var(--o-accent, #ea580c);
    box-shadow: 0 0 0 0.2rem color-mix(in srgb, var(--o-accent, #ea580c) 20%, transparent);
  }
  .ao-label {
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #9ca3af;
    margin-bottom: 3px;
  }
  .ao-coupon {
    background: #f0fdf4;
    border: 1px dashed #86efac;
  }

  /* Payment summary cards */
  .ao-pay-summary {
    border-radius: 16px;
    border-left: 6px solid #e5e7eb !important;
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      box-shadow 0.25s ease;
  }
  .ao-pay-summary:hover { transform: translateY(-4px); }
  .ao-pay-summary.received { border-left-color: #22c55e !important; }
  .ao-pay-summary.received:hover { box-shadow: 0 18px 34px rgba(34,197,94,0.24) !important; }
  .ao-pay-summary.pending { border-left-color: #f59e0b !important; }
  .ao-pay-summary.pending:hover { box-shadow: 0 18px 34px rgba(245,158,11,0.26) !important; }
  .ao-pay-summary.total { border-left-color: #64748b !important; }
  .ao-pay-summary.total:hover { box-shadow: 0 18px 34px rgba(100,116,139,0.22) !important; }
  .ao-pay-emoji {
    font-size: 1.9rem;
    line-height: 1;
    transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  .ao-pay-summary:hover .ao-pay-emoji { transform: scale(1.18) rotate(-8deg); }
  /* Money still to come keeps a slow pulse — it is the one that needs chasing */
  .ao-pay-summary.pending .ao-pay-emoji {
    animation: aoIconBob 3.4s ease-in-out infinite;
  }
  .ao-pay-amount {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.1;
  }
  .ao-pay-caption {
    font-size: 0.78rem;
    font-weight: 600;
    color: #6b7280;
  }
  .ao-pay-count {
    display: inline-block;
    margin-left: 8px;
    background: #f1f5f9;
    color: #475569;
    border-radius: 30px;
    padding: 1px 9px;
    font-size: 0.68rem;
  }

  /* Per-order payment chip */
  .ao-pay-chip {
    display: inline-block;
    border-radius: 10px;
    padding: 6px 10px;
    margin-top: 2px;
    font-size: 0.8rem;
  }
  .ao-pay-chip.is-received {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #15803d;
  }
  .ao-pay-chip.is-pending {
    background: #fffbeb;
    border: 1px solid #fde68a;
    color: #b45309;
  }
  .ao-pay-chip small { color: inherit; opacity: 0.85; }

  @media (prefers-reduced-motion: reduce) {
    .ao-hero,
    .ao-hero::before,
    .ao-hero::after,
    .ao-hero-aurora,
    .ao-mote,
    .ao-hero-icon,
    .ao-hero-title-text,
    .ao-chip.is-active,
    .ao-order-card,
    .ao-card-strip,
    .ao-status-dot,
    .ao-pay-pill.is-pending,
    .ao-step.is-current,
    .ao-pay-summary.pending .ao-pay-emoji,
    .ao-del-btn:hover:not(:disabled) .ao-del-ico { animation: none !important; }
    .ao-motes { display: none; }
    .ao-hero-title-text {
      -webkit-text-fill-color: #fff;
      color: #fff;
    }
  }
`;

export default AdminOrdersPage;
